import styled from 'styled-components';
import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { push, push as Push } from 'redux-first-history';
import { noop } from 'lodash-es';
import {
    DialogBody,
    DotsIcon,
    EmptyState,
    MobileCardRowsProps,
    MobileTable,
    palette,
    Paper,
    SimplePopover,
    SimplePopoverPlainOption,
    SimpleTable,
    SimpleTableHeadersProps,
    SimpleTableRowsProps,
} from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';
import { AccessLevel, CompressionFormat, ExportFileFormats, Segment } from '@dev/ee-api/ee-api-models';

import useMobile from '@/services/hooks/useMobile';
import { parseLegacyUrl } from '@/services/tools/parseLegacyUrl';
import { ContactsPaths, pathsParams } from '@/services/dictionaries/Paths';
import { ALL_CONTACTS_SEGMENT, DEFAULT_PER_PAGE_COUNT } from '@/services/constants';
import { getSegmentsStore, SegmentAddStart, SegmentDeleteStart } from '@/store/segments/actions';
import { getConfigStore } from '@/store/config/types';
import { ShowDialogParams } from '@/store/dialog/types';
import { DisableDialog, HideDialog, ShowDialog } from '@/store/dialog/actions';
import { getListstStore, ListCreateNthStart, ListCreateRandomStart, ListsGetNamesStart } from '@/store/lists/actions';
import { ExportContactsParams } from '@/store/exports/types';
import { ExportContactsStart } from '@/store/exports/actions';
import { SetCampaignCreatorOpen } from '@/store/campaigns/actions';
import { getAccountStore } from '@/store/account/actions';

import { CopyListOrSegment } from '../../../CommonActions/CopyListOrSegment';
import { ExportContacts } from '../../../CommonActions/Export';
import { RandomNthSelection } from '../../../CommonActions/RandomNth';
import { useTableHeaders } from '../../hooks/useTableHeaders';
import { useTableRows } from '../../hooks/useTableRows';
import { useRequestParams } from '../../hooks/useRequestParams';
import { DropdownOptions, HandleSegmentMenuClickParams, SingleSegmentMenuInterface } from '../../SegmentsInterface';

const MobileTableContainer = styled.div`
    margin-top: 24px;

    & > div {
        padding-bottom: 2px;
    }
    & .MuiContainer-root.MuiContainer-maxWidthLg {
        padding: 0 20px;
    }
    & .iconWrapperCheckBlue {
        display: block;
    }
`;

const SingleMenuContainer = styled.div`
    div .icon--color {
        fill: ${palette.gray[6]};
    }
`;

const handleSegmentMenuClick = (props: HandleSegmentMenuClickParams): void => {
    const { activePage, dispatch, lists, option, perPage, searchName, segment, t } = props;
    const name = safelyEncodeBase64Url(segment.Name);

    const handleCopySegment = (segment: Segment): void => {
        const newSegment = {
            Name: `${segment.Name} ${t('copy')}`,
            rule: segment.Rule,
        };
        let newName = newSegment.Name;

        const updateSelectedName = (inputName: string): void => {
            newName = inputName;
        };

        const params: ShowDialogParams = {
            title: t('copysegment'),
            confirmLabel: t('copy'),
            onClickConfirm: (): void => {
                dispatch(SegmentAddStart(newName, newSegment.rule));
            },
            cancelLabel: t('cancel'),
            body: <CopyListOrSegment selected={newSegment} isList={false} onChange={updateSelectedName} />,
        };

        dispatch(ShowDialog(params));
    };

    const handleDeleteSegment = (segment: Segment): void => {
        const params: ShowDialogParams = {
            title: `${t('delete')} ${t('segment')}`,
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(SegmentDeleteStart(segment.Name, perPage, activePage, searchName));
            },
            cancelLabel: t('cancel'),
            body: (
                <DialogBody variant={'info'} above={t('deletesegmentmsgbasic')}>
                    {t('deletecontactsstaymsg')}
                </DialogBody>
            ),
        };

        dispatch(ShowDialog(params));
    };

    const handleAddRandomNthList = (single: boolean, selected: Segment): void => {
        if (lists && !lists.listNamesCollection) {
            dispatch(ListsGetNamesStart());
        }
        let newList = { ...lists.newList };
        const prepareParams = (list): void => {
            newList = { ...newList, ...list };
        };
        const params: ShowDialogParams = {
            title: single ? t('createrandomlist') : t('createnthselectionlists'),
            confirmLabel: t('save'),
            isHiddenManually: !single,
            onClickConfirm: (): void => {
                const listCount = lists?.listNamesCollection?.length || lists?.totalCount || 0;
                if (single) {
                    dispatch(ListCreateRandomStart(newList));
                    return;
                }

                dispatch(ListCreateNthStart(newList));
                dispatch(DisableDialog(true));

                const checkIfNthSelectionAreCreated = setInterval(() => {
                    if (lists?.isNthListCreated) {
                        return;
                    }
                    dispatch(HideDialog());
                    dispatch(
                        Push(
                            `${ContactsPaths.Lists}?page=${
                                Math.floor((listCount + 1) / DEFAULT_PER_PAGE_COUNT) + 1
                            }&perPage=${DEFAULT_PER_PAGE_COUNT}`
                        )
                    );
                    clearInterval(checkIfNthSelectionAreCreated);
                }, 1000);
            },
            cancelLabel: t('cancel'),
            body: (
                <RandomNthSelection
                    singlelist={single}
                    selected={selected}
                    newList={lists.newList}
                    onPrepareParams={prepareParams}
                />
            ),
            isDisabled: true,
        };

        dispatch(ShowDialog(params));
    };

    const handleExportContacts = (selected): void => {
        const exportedData: ExportContactsParams = {
            fileName: selected.Name,
            rule: selected.IsAllContacts ? null : selected.Rule,
            fileFormat: ExportFileFormats.Csv,
            emails: [],
            compressionFormat: CompressionFormat.Zip,
        };
        const params: ShowDialogParams = {
            title: t('export'),
            confirmLabel: t('export'),
            onClickConfirm: (): void => {
                dispatch(ExportContactsStart(exportedData));
            },
            cancelLabel: t('cancel'),
            body: <ExportContacts selected={exportedData} />,
        };

        dispatch(ShowDialog(params));
    };

    switch (option) {
        case DropdownOptions.ViewContacts:
            dispatch(
                Push(
                    ContactsPaths.SingleSegment.replace(
                        pathsParams.segmentName,
                        segment.IsAllContacts
                            ? safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)
                            : safelyEncodeBase64Url(segment.Name)
                    )
                )
            );
            break;
        case DropdownOptions.Edit:
            dispatch(Push(ContactsPaths.SegmentEditor.replace(pathsParams.segmentName, name)));
            break;
        case DropdownOptions.Delete:
            handleDeleteSegment(segment);
            break;
        case DropdownOptions.Copy:
            handleCopySegment(segment);
            break;
        case DropdownOptions.Export:
            handleExportContacts(segment);
            break;
        case DropdownOptions.Randomlist:
            handleAddRandomNthList(true, segment);
            break;
        case DropdownOptions.Nthselectionlists:
            handleAddRandomNthList(false, segment);
            break;
        default:
            break;
    }
};

export const SingleSegmentMenu: FC<SingleSegmentMenuInterface> = (props: SingleSegmentMenuInterface): JSX.Element => {
    const { index, segment } = props;
    const account = useSelector(getAccountStore);
    const lists = useSelector(getListstStore);
    const params = useRequestParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const enableContactFeatures = account?.GeneralInfo?.EnableContactFeatures;
    const canExport = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.Export);
    const canModifyContacts = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyContacts);

    const options = [
        {
            value: DropdownOptions.Edit,
            label: t('edit'),
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: DropdownOptions.Copy,
            label: t('copy'),
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: DropdownOptions.Export,
            label: t('export'),
            available: canExport,
        },
        {
            value: DropdownOptions.Randomlist,
            label: t('randomlist'),
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: DropdownOptions.Nthselectionlists,
            label: t('nthselectionlists'),
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: DropdownOptions.Delete,
            label: t('delete'),
            available: canModifyContacts,
        },
    ];
    const allContactsOptions = [
        {
            value: DropdownOptions.Export,
            label: t('export'),
            available: canExport,
        },
        {
            value: DropdownOptions.Randomlist,
            label: t('randomlist'),
            available: canModifyContacts && enableContactFeatures,
        },
        {
            value: DropdownOptions.Nthselectionlists,
            label: t('nthselectionlists'),
            available: canModifyContacts && enableContactFeatures,
        },
    ];

    const menuoptions = (segment.IsAllContacts ? allContactsOptions : options)
        ?.filter((option): boolean => option.available)
        ?.map((option): { value: string; label: string } => ({
            value: option.value,
            label: option.label,
        }));
    return (
        <SingleMenuContainer>
            {menuoptions.length > 0 && (
                <SimplePopover
                    id={`more-actions-row-${index}`}
                    type="icon"
                    options={menuoptions}
                    onItemClick={(event, obj): void => {
                        event.preventDefault();
                        const handleClickParams = {
                            option: obj.value as string,
                            segment,
                            perPage: params.perPage,
                            activePage: params.page,
                            searchName: params.searchName,
                            lists,
                            dispatch,
                            t,
                        };
                        handleSegmentMenuClick(handleClickParams);
                    }}
                    placement={'bottom-end'}
                >
                    <DotsIcon color={palette.gray[6]} />
                </SimplePopover>
            )}
        </SingleMenuContainer>
    );
};

export const TableComponent: FC = (): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const segments = useSelector(getSegmentsStore);
    const lists = useSelector(getListstStore);
    const account = useSelector(getAccountStore);
    const config = useSelector(getConfigStore);
    const rows = useTableRows();
    const headers = useTableHeaders();
    const params = useRequestParams();

    const getDropdownMobileTableOptions = (segment?: Segment): SimplePopoverPlainOption[] => {
        const canExport = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.Export);
        const canModifyContacts = account.GeneralInfo.AccountAccessLevel.includes(AccessLevel.ModifyContacts);
        const enableContactFeatures = account?.GeneralInfo?.EnableContactFeatures;

        let allContactsOptions;
        const options = [
            {
                value: DropdownOptions.ViewContacts,
                label: t('viewcontacts'),
                available: canModifyContacts && enableContactFeatures,
            },
            {
                value: DropdownOptions.Export,
                label: t('export'),
                available: canExport,
            },
            {
                value: DropdownOptions.Randomlist,
                label: t('randomlist'),
                available: canModifyContacts && enableContactFeatures,
            },
            {
                value: DropdownOptions.Nthselectionlists,
                label: t('nthselectionlists'),
                available: canModifyContacts && enableContactFeatures,
            },
            {
                value: DropdownOptions.Delete,
                label: t('delete'),
                available: canModifyContacts,
            },
        ];
        if (segment?.IsAllContacts) {
            allContactsOptions = options.filter((el) => el.value !== DropdownOptions.Delete);
        }

        return (segment?.IsAllContacts ? allContactsOptions : options)
            ?.filter((option): boolean => option.available)
            ?.map((option): { value: string; label: string } => ({
                value: option.value,
                label: option.label,
            }));
    };

    const dropdownDataFilter = (
        options: SimplePopoverPlainOption[],
        returnOnItemClick: Segment
    ): SimplePopoverPlainOption[] => getDropdownMobileTableOptions(returnOnItemClick);

    const dropdownOptions = getDropdownMobileTableOptions();

    if (isMobile) {
        if (!segments?.isLoadingSegments && !segments?.currentCollectionTotalCount) {
            return (
                <Paper>
                    <EmptyState text={t('emptystate')} />
                </Paper>
            );
        }
        return (
            <MobileTableContainer>
                <MobileTable
                    rows={rows as MobileCardRowsProps[]}
                    headers={headers as string[]}
                    dropdownData={dropdownOptions}
                    dropdownDataFilter={dropdownDataFilter}
                    buttonText={t('sendcampaign')}
                    disableSimplePopover={!dropdownOptions.length}
                    onClickButton={(event, segment): void => {
                        event.preventDefault();
                        dispatch(
                            Push(
                                parseLegacyUrl(
                                    ContactsPaths.CampaignCreatorTargetOld.replace(
                                        pathsParams.listName,
                                        segment.returnOnItemClick.IsAllContacts
                                            ? safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)
                                            : safelyEncodeBase64Url(segment.returnOnItemClick.Name)
                                    ),
                                    config.isStandalone
                                )
                            )
                        );
                    }}
                    onItemClick={(ev, value, item) => {
                        const handleClickParams = {
                            option: value.value as string,
                            segment: item,
                            perPage: params.perPage,
                            activePage: params.page,
                            searchName: params.searchName,
                            lists,
                            dispatch,
                            t,
                        };
                        handleSegmentMenuClick(handleClickParams);
                    }}
                />
            </MobileTableContainer>
        );
    }
    return (
        <SimpleTable
            columnWidth={{
                name: '25%',
                rule: '35%',
                tracking: '130px',
            }}
            headers={headers as SimpleTableHeadersProps[]}
            stickyHeader={true}
            rows={rows as SimpleTableRowsProps[]}
            isEmpty={rows.length === 0 && !segments.isLoadingSegments}
            onClickMenu={noop}
            onClickIcon={(event, segment): void => {
                event.preventDefault();

                if (config.isStandalone) {
                    dispatch(SetCampaignCreatorOpen(true, segment.IsAllContacts ? t('allcontacts') : segment.name));
                } else {
                    dispatch(
                        Push(
                            parseLegacyUrl(
                                ContactsPaths.CampaignCreatorTargetOld.replace(
                                    pathsParams.listName,
                                    segment.IsAllContacts
                                        ? safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)
                                        : safelyEncodeBase64Url(segment.name)
                                ),
                                config.isStandalone
                            )
                        )
                    );
                }
            }}
            onClickRow={(e, segment): void => {
                dispatch(
                    Push(
                        ContactsPaths.SingleSegment.replace(
                            pathsParams.segmentName,
                            segment.IsAllContacts
                                ? safelyEncodeBase64Url(ALL_CONTACTS_SEGMENT)
                                : safelyEncodeBase64Url(segment.id)
                        )
                    )
                );
            }}
            emptyStateProps={{
                text: t('emptystate'),
            }}
        />
    );
};
